import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Textarea } from '../../../components/ui/textarea';
import { Label } from '../../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Sparkles, Wand2 } from 'lucide-react';
import { themeApi } from './themeApi';
import { toast } from 'sonner';

interface AiGenerationModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const LOADING_MESSAGES = [
  "Analyzing brand identity...",
  "Selecting the perfect layout...",
  "Writing compelling copy...",
  "Applying your color palette...",
  "Generating responsive sections...",
  "Assembling your theme...",
];

export function AiGenerationModal({ open, onOpenChange }: AiGenerationModalProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [loadingMsgIdx, setLoadingMsgIdx] = useState(0);

  const [formData, setFormData] = useState({
    name: '',
    industry: '',
    description: '',
    tone: 'professional',
    primaryColor: '#000000',
    secondaryColor: '#ffffff',
    headingFont: 'Inter',
  });

  const [customError, setCustomError] = useState<string | null>(null);

  const generateMutation = useMutation({
    mutationFn: () => themeApi.generateAiTheme(formData),
    onMutate: () => {
      setCustomError(null);
      let idx = 0;
      const interval = setInterval(() => {
        idx = (idx + 1) % LOADING_MESSAGES.length;
        setLoadingMsgIdx(idx);
      }, 3000);
      return { interval };
    },
    onSuccess: (data, _, context) => {
      clearInterval(context?.interval);
      toast.success('AI Theme generated successfully!');
      queryClient.invalidateQueries({ queryKey: ['store-themes'] });
      onOpenChange(false);
      // Navigate to the editor for the new theme
      navigate(`/store/themes/${data.themeId}/editor`);
    },
    onError: (error: any, _, context) => {
      clearInterval(context?.interval);
      const msg = error.response?.data?.error?.message || 'Failed to generate theme';
      if (error.response?.status === 422) {
        setCustomError(msg);
      } else {
        toast.error(msg);
      }
    }
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name || !formData.industry) return;
    generateMutation.mutate();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        {generateMutation.isPending ? (
          <div className="py-12 flex flex-col items-center justify-center space-y-6">
            <div className="relative">
              <div className="absolute inset-0 animate-ping rounded-full bg-primary/20" />
              <Wand2 className="h-12 w-12 text-primary animate-pulse relative z-10" />
            </div>
            <div className="text-center space-y-2">
              <h3 className="text-xl font-semibold">Building with AI</h3>
              <p className="text-muted-foreground animate-pulse">
                {LOADING_MESSAGES[loadingMsgIdx]}
              </p>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-primary" />
                Build with AI
              </DialogTitle>
              <DialogDescription>
                Describe your brand, and our AI will generate a complete, customized theme for your store.
              </DialogDescription>
            </DialogHeader>

            <div className="grid gap-4 py-4">
              {customError && (
                <div className="p-3 text-sm text-destructive bg-destructive/10 rounded-md border border-destructive/20">
                  {customError}
                </div>
              )}
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Brand Name</Label>
                  <Input 
                    id="name" 
                    value={formData.name} 
                    onChange={e => setFormData({ ...formData, name: e.target.value })} 
                    placeholder="e.g. Acme Co." 
                    required 
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="industry">Industry</Label>
                  <Input 
                    id="industry" 
                    value={formData.industry} 
                    onChange={e => setFormData({ ...formData, industry: e.target.value })} 
                    placeholder="e.g. Apparel, Electronics" 
                    required 
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Brand Description & Offerings</Label>
                <Textarea 
                  id="description" 
                  value={formData.description} 
                  onChange={e => setFormData({ ...formData, description: e.target.value })} 
                  placeholder="Describe what you sell, who your target audience is, and what makes your brand unique..." 
                  rows={4}
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Brand Tone</Label>
                  <Select value={formData.tone} onValueChange={v => setFormData({ ...formData, tone: v || '' })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select tone" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="professional">Professional & Trustworthy</SelectItem>
                      <SelectItem value="playful">Playful & Energetic</SelectItem>
                      <SelectItem value="minimalist">Minimalist & Elegant</SelectItem>
                      <SelectItem value="bold">Bold & Edgy</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Heading Font</Label>
                  <Select value={formData.headingFont} onValueChange={v => setFormData({ ...formData, headingFont: v || '' })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select font" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Inter">Inter (Sans-serif)</SelectItem>
                      <SelectItem value="Playfair Display">Playfair Display (Serif)</SelectItem>
                      <SelectItem value="Outfit">Outfit (Modern)</SelectItem>
                      <SelectItem value="Roboto Mono">Roboto Mono (Monospace)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="primaryColor">Primary Color</Label>
                  <div className="flex gap-2">
                    <Input 
                      type="color" 
                      id="primaryColor"
                      className="w-12 p-1"
                      value={formData.primaryColor} 
                      onChange={e => setFormData({ ...formData, primaryColor: e.target.value })} 
                    />
                    <Input 
                      value={formData.primaryColor} 
                      onChange={e => setFormData({ ...formData, primaryColor: e.target.value })} 
                      pattern="^#[0-9A-Fa-f]{6}$"
                      placeholder="#000000"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="secondaryColor">Secondary Color</Label>
                  <div className="flex gap-2">
                    <Input 
                      type="color" 
                      id="secondaryColor"
                      className="w-12 p-1"
                      value={formData.secondaryColor} 
                      onChange={e => setFormData({ ...formData, secondaryColor: e.target.value })} 
                    />
                    <Input 
                      value={formData.secondaryColor} 
                      onChange={e => setFormData({ ...formData, secondaryColor: e.target.value })} 
                      pattern="^#[0-9A-Fa-f]{6}$"
                      placeholder="#ffffff"
                    />
                  </div>
                </div>
              </div>

            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button type="submit" disabled={!formData.name || !formData.industry}>
                <Wand2 className="mr-2 h-4 w-4" />
                Generate Theme
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
